// core/services/firebase/storageService.ts
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './config';
import imageCompression from 'browser-image-compression';

export interface UploadOptions {
  path: string; // ex: 'tenants/123/profiles/avatar.jpg'
  compress?: boolean;
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
}

export const storageService = {
  /**
   * Upload de arquivo genérico (com compressão opcional para imagens)
   */
  async uploadFile(file: File, options: UploadOptions): Promise<string> {
    let fileToUpload = file;

    // Compressão automática para imagens
    if (options.compress && file.type.startsWith('image/')) {
      try {
        console.log(`Original size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
        
        const compressionOptions = {
          maxSizeMB: options.maxSizeMB || 1, // Default 1MB
          maxWidthOrHeight: options.maxWidthOrHeight || 1920, // Default Full HD
          useWebWorker: true,
          initialQuality: 0.8
        };

        fileToUpload = await imageCompression(file, compressionOptions);
        
        console.log(`Compressed size: ${(fileToUpload.size / 1024 / 1024).toFixed(2)} MB`);
      } catch (error) {
        console.warn('Image compression failed, uploading original file.', error);
      }
    }

    // Cria referência
    const storageRef = ref(storage, options.path);
    
    // Upload
    await uploadBytes(storageRef, fileToUpload);
    
    // Retorna URL pública
    return await getDownloadURL(storageRef);
  },

  /**
   * Deleta arquivo
   */
  async deleteFile(url: string): Promise<void> {
    const storageRef = ref(storage, url);
    await deleteObject(storageRef);
  },

  /**
   * Validar arquivo antes do envio (Tamanho e Tipo)
   */
  validateFile(file: File, type: 'image' | 'video'): { valid: boolean; error?: string } {
    const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
    const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB (antes da compressão)

    if (type === 'image') {
      if (!file.type.startsWith('image/')) return { valid: false, error: 'Arquivo não é uma imagem.' };
      if (file.size > MAX_IMAGE_SIZE) return { valid: false, error: 'Imagem muito grande (Max 10MB).' };
    }

    if (type === 'video') {
      if (!file.type.startsWith('video/')) return { valid: false, error: 'Arquivo não é um vídeo.' };
      if (file.size > MAX_VIDEO_SIZE) return { valid: false, error: 'Vídeo muito grande (Max 50MB).' };
    }

    return { valid: true };
  }
};
